import {
  useSuperAdminShops,
  useSuperAdminShopsCatalog,
} from "@/api/super-admin/use-super-admin-shops";
import { useTrackingErrorsSummary } from "@/api/super-admin/use-tracking-queue-errors";
import { useRecentSuperAdminActions } from "@babackapp/private-api-client/client/use-super-admin-actions";
import {
  useMonitoringEvents,
  useMonitoringEventsSummary,
} from "@babackapp/private-api-client/client/use-super-admin-monitoring-events";
import { ArrowRight, BellRing } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AdminPageShell } from "./admin-tab-pages";
import { categoryLabel } from "./monitoring-events-page";
import { formatRelative } from "./shop-shared";
import { buildWatchList } from "./watch-list";
import { WatchListPanel } from "./watch-list-panel";

function percent(part: number, total: number): string {
  return total > 0 ? `${((part / total) * 100).toFixed(1)} %` : "—";
}

function Panel({
  title,
  icon,
  action,
  className,
  children,
}: {
  title: string;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={`rounded-lg border bg-background ${className ?? ""}`}>
      <header className="flex items-center justify-between gap-2 border-b px-4 py-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          {icon}
          {title}
        </h2>
        {action}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function SeeAll({ to }: { to: string }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 text-xs font-medium text-violet-700 hover:underline"
    >
      Tout voir <ArrowRight className="h-3 w-3" />
    </Link>
  );
}

/**
 * What needs a look today, not the fleet table: health of the last 24 h,
 * shops to watch, newcomers and what the team changed.
 */
export function AdminDashboardPage() {
  const overview = useSuperAdminShops();
  const catalog = useSuperAdminShopsCatalog();
  const eventsSummary = useMonitoringEventsSummary({ hours: 24 });
  const lastErrors = useMonitoringEvents({ severity: "ERROR" });
  const carrierErrors = useTrackingErrorsSummary({ isResolved: false });
  const actions = useRecentSuperAdminActions(10);

  const shops = useMemo(() => overview.data?.shops ?? [], [overview.data]);
  // Frozen at mount: the lists below must not reshuffle on every render.
  const [now] = useState(() => Date.now());

  const fleet = useMemo(() => {
    const counted = shops.filter(
      (shop) => !shop.isTestShop && shop.isIncludedInStats !== false,
    );
    const sum = (key: keyof (typeof counted)[number]) =>
      counted.reduce((total, shop) => total + Number(shop[key] ?? 0), 0);
    return {
      returns24h: sum("returnRequestsLast24Hours"),
      orders24h: sum("ordersLast24Hours"),
      returns7d: sum("returnRequestsLast7Days"),
      orders7d: sum("ordersLast7Days"),
      returns30d: sum("returnRequestsLast30Days"),
      orders30d: sum("ordersLast30Days"),
      active7d: counted.filter((shop) => shop.returnRequestsLast7Days > 0)
        .length,
      shopsCount: counted.length,
    };
  }, [shops]);

  const watchList = useMemo(
    () => buildWatchList(shops, catalog.data?.catalogs ?? [], now),
    [shops, catalog.data, now],
  );

  const errors = (lastErrors.data?.pages[0]?.events ?? []).slice(0, 5);
  const openCarrierErrors = carrierErrors.data?.total ?? null;

  return (
    <AdminPageShell
      title="Dashboard"
      description="Ce qui demande un coup d'œil aujourd'hui. Le détail de chaque boutique est dans Boutiques."
    >
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          {
            label: "Retours 24 h",
            value: fleet.returns24h,
            sub: `${percent(fleet.returns24h, fleet.orders24h)} des commandes`,
          },
          {
            label: "Retours 7 j",
            value: fleet.returns7d,
            sub: `${percent(fleet.returns7d, fleet.orders7d)} des commandes`,
          },
          {
            label: "Retours 30 j",
            value: fleet.returns30d,
            sub: `${percent(fleet.returns30d, fleet.orders30d)} des commandes`,
          },
          {
            label: "Boutiques actives 7 j",
            value: fleet.active7d,
            sub: `sur ${fleet.shopsCount} en production`,
          },
        ].map((kpi) => (
          <div key={kpi.label} className="rounded-lg border bg-background p-4">
            <div className="text-xs font-medium text-muted-foreground">
              {kpi.label}
            </div>
            <div className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
              {overview.isPending ? "…" : kpi.value.toLocaleString("fr-FR")}
            </div>
            <div className="text-xs text-muted-foreground">{kpi.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Panel
          title="Santé, dernières 24 h"
          className={watchList.length === 0 ? "xl:col-span-2" : undefined}
          icon={<BellRing className="h-4 w-4 text-muted-foreground" />}
          action={<SeeAll to="/admin/events" />}
        >
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <Link
              to="/admin/carrier-issues"
              className="rounded-md border p-3 hover:border-violet-400"
            >
              <div className="text-xs font-medium text-muted-foreground">
                Erreurs de suivi ouvertes
              </div>
              <div
                className={`mt-1 text-xl font-semibold tabular-nums ${openCarrierErrors ? "text-red-700" : "text-foreground"}`}
              >
                {openCarrierErrors === null
                  ? "…"
                  : openCarrierErrors.toLocaleString("fr-FR")}
              </div>
            </Link>
            {(eventsSummary.data?.categories ?? []).map((item) => (
              <Link
                key={item.category}
                to={`/admin/events?category=${item.category}`}
                className="rounded-md border p-3 hover:border-violet-400"
              >
                <div className="truncate text-xs font-medium text-muted-foreground">
                  {categoryLabel(item.category)}
                </div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-xl font-semibold tabular-nums text-foreground">
                    {item.events}
                  </span>
                  {item.errors > 0 && (
                    <span className="text-xs font-medium text-red-700">
                      {item.errors} err.
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
          {eventsSummary.data?.categories.length === 0 && (
            <p className="mt-3 text-sm text-muted-foreground">
              Aucun événement sur les dernières 24 h.
            </p>
          )}
          {errors.length > 0 && (
            <ul className="mt-4 divide-y border-t">
              {errors.map((alert) => (
                <li key={alert.id} className="flex items-start gap-2 py-2">
                  <span
                    className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-red-500"
                    aria-hidden
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm text-foreground">
                      {alert.title}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatRelative(new Date(alert.createdAt).getTime())}
                      {alert.shopName && ` · ${alert.shopName}`}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <WatchListPanel
          items={watchList}
          onChanged={() => {
            void overview.refetch();
            void catalog.refetch();
          }}
        />

        <Panel
          title="Dernières actions des super admins"
          className="xl:col-span-2"
        >
          {actions.isPending ? (
            <p className="text-sm text-muted-foreground">Chargement…</p>
          ) : (actions.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Aucune écriture ni export récents.
            </p>
          ) : (
            <ul className="divide-y">
              {(actions.data ?? []).map((action) => (
                <li key={action.id} className="py-2">
                  <p className="truncate text-sm text-foreground">
                    <span className="font-medium">{action.userEmail}</span>
                    {" · "}
                    <span className="font-mono text-xs">
                      {action.method} {action.route}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatRelative(new Date(action.createdAt).getTime())}
                    {" · "}
                    {action.kind === "EXPORT" ? "export" : "écriture"}
                    {" · "}
                    <Link
                      to={`/admin/shops/${action.shopId}`}
                      className="text-violet-700 hover:underline"
                    >
                      {action.shopName ?? action.shopId}
                    </Link>
                    {action.detail && ` · ${action.detail}`}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </AdminPageShell>
  );
}
